import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Send as SubmitIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

const StyledCard = styled(Card)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: "blur(10px)",
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[10],
}));

const TimesheetTab: React.FC = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [hours, setHours] = useState("");
  const [description, setDescription] = useState("");

  const currentWeek = {
    startDate: "2024-01-08",
    endDate: "2024-01-14",
    status: "Draft",
    totalHours: 32,
  };

  const weeklyData = [
    { day: "Monday", date: "2024-01-08", hours: 8, description: "Development work", project: "Project A" },
    { day: "Tuesday", date: "2024-01-09", hours: 8, description: "Code review and testing", project: "Project A" },
    { day: "Wednesday", date: "2024-01-10", hours: 8, description: "Bug fixes and documentation", project: "Project B" },
    { day: "Thursday", date: "2024-01-11", hours: 8, description: "Client meetings and planning", project: "Project A" },
    { day: "Friday", date: "2024-01-12", hours: 0, description: "", project: "" },
    { day: "Saturday", date: "2024-01-13", hours: 0, description: "", project: "" },
    { day: "Sunday", date: "2024-01-14", hours: 0, description: "", project: "" },
  ];

  const previousWeeks = [
    {
      week: "Jan 1 - Jan 7, 2024",
      totalHours: 40,
      status: "Approved",
      submittedDate: "2024-01-08",
    },
    {
      week: "Dec 25 - Dec 31, 2023",
      totalHours: 24,
      status: "Approved",
      submittedDate: "2024-01-02",
    },
    {
      week: "Dec 18 - Dec 24, 2023",
      totalHours: 40,
      status: "Approved",
      submittedDate: "2023-12-25",
    },
  ];

  const monthlyStats = {
    totalHours: 152,
    expectedHours: 160,
    utilization: 95,
    overtime: 8,
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "success";
      case "submitted":
        return "info";
      case "draft":
        return "warning";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const handleAddEntry = () => {
    console.log("Adding timesheet entry:", { selectedDate, hours, description });
    setOpenDialog(false);
    setSelectedDate("");
    setHours("");
    setDescription("");
  };

  const handleSubmitTimesheet = () => {
    console.log("Submitting timesheet for approval");
  };

  const handleSaveTimesheet = () => {
    console.log("Saving timesheet as draft");
  };

  return (
    <Grid container spacing={3}>
      {/* Monthly Stats */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
          <ScheduleIcon color="primary" sx={{ mr: 1 }} />
          Monthly Overview
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent sx={{ textAlign: "center" }}>
            <Typography variant="h4" color="primary" gutterBottom>
              {monthlyStats.totalHours}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Hours This Month
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent sx={{ textAlign: "center" }}>
            <Typography variant="h4" color="info.main" gutterBottom>
              {monthlyStats.utilization}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Utilization Rate
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent sx={{ textAlign: "center" }}>
            <Typography variant="h4" color="success.main" gutterBottom>
              {monthlyStats.expectedHours}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Expected Hours
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent sx={{ textAlign: "center" }}>
            <Typography variant="h4" color="warning.main" gutterBottom>
              {monthlyStats.overtime}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Overtime Hours
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      {/* Current Week Timesheet */}
      <Grid item xs={12}>
        <StyledCard>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Current Week: {currentWeek.startDate} - {currentWeek.endDate}
              </Typography>
              <Box display="flex" gap={1}>
                <Chip
                  label={currentWeek.status}
                  color={getStatusColor(currentWeek.status) as any}
                  variant="outlined"
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenDialog(true)}
                >
                  Add Entry
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveTimesheet}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SubmitIcon />}
                  onClick={handleSubmitTimesheet}
                >
                  Submit
                </Button>
              </Box>
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                    <TableCell><strong>Day</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Hours</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Project</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {weeklyData.map((entry, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.05) },
                        backgroundColor: entry.hours === 0 ? alpha(theme.palette.grey[300], 0.3) : "inherit",
                      }}
                    >
                      <TableCell>{entry.day}</TableCell>
                      <TableCell>{entry.date}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={entry.hours === 0 ? "text.secondary" : "text.primary"}
                        >
                          {entry.hours || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={entry.description ? "text.primary" : "text.secondary"}
                        >
                          {entry.description || "No entry"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={entry.project ? "text.primary" : "text.secondary"}
                        >
                          {entry.project || "-"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                    <TableCell colSpan={2}><strong>Total</strong></TableCell>
                    <TableCell><strong>{currentWeek.totalHours} hours</strong></TableCell>
                    <TableCell colSpan={2}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </StyledCard>
      </Grid>

      {/* Previous Timesheets */}
      <Grid item xs={12}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Previous Timesheets
            </Typography>
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                    <TableCell><strong>Week</strong></TableCell>
                    <TableCell><strong>Total Hours</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Submitted Date</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previousWeeks.map((week, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{ "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.05) } }}
                    >
                      <TableCell>{week.week}</TableCell>
                      <TableCell>{week.totalHours}</TableCell>
                      <TableCell>
                        <Chip
                          label={week.status}
                          color={getStatusColor(week.status) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{week.submittedDate}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </StyledCard>
      </Grid>

      {/* Add Entry Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Timesheet Entry</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Hours"
                type="number"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                inputProps={{ min: 0, max: 24, step: 0.5 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleAddEntry} variant="contained">
            Add Entry
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default TimesheetTab;